import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { Card, SectionTitle, Spinner, ErrorNote } from '../components/ui';
import PdfPreview from '../components/PdfPreview';

export default function AgreementComposer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast, run } = useApp();

  const { data, error, loading, reload } = useAsync(
    () => Promise.all([api.get('/projects/' + id), api.get('/settings')]), [id],
  );

  const [agreement, setAgreement] = useState(null);
  const [terms, setTerms] = useState('');
  const [payment, setPayment] = useState('');
  const [exclusions, setExclusions] = useState('');
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!data) return;
    const [p, settings] = data;
    const a = p.agreement;
    setAgreement(a || null);
    setTerms(a?.termsText || settings.defaultTerms || '');
    setPayment(a?.paymentScheduleText || `${settings.defaultDepositPct}% deposit on signing, balance on completion.`);
    setExclusions(a?.exclusions || '');
  }, [data]);

  if (loading) return <Spinner label="Loading agreement" />;
  if (error) return <div className="view"><ErrorNote onRetry={reload}>{error}</ErrorNote></div>;
  const [project] = data;

  const body = () => ({ termsText: terms, paymentScheduleText: payment, exclusions });

  async function saveAgreement() {
    const saved = agreement
      ? await api.put('/agreements/' + agreement.id, body())
      : await api.post('/agreements', { projectId: id, ...body() });
    setAgreement(saved);
    return saved;
  }

  const onPreview = () => run(async () => {
    setBusy(true);
    const a = await saveAgreement();
    setBusy(false);
    setPreviewUrl(`/api/a/${a.token}/pdf`);
  });

  const onSend = () => run(async () => {
    setBusy(true);
    const a = await saveAgreement();
    const r = await api.post(`/agreements/${a.id}/send`);
    navigator.clipboard?.writeText(`${location.origin}/a/${r.agreement.token}`).catch(() => {});
    setBusy(false);
    if (!r.email) toast('Link copied — no email on file, so nothing was sent');
    else if (!r.email.delivered) toast(`Link copied, but the email failed: ${r.email.error || 'unknown error'}`);
    else toast('Agreement sent for signature');
    navigate(project.standalone ? '/documents' : `/project/${id}`);
  });

  return (
    <>
      <PageHeader
        title="Agreement"
        sub={`${project.number} · ${project.customer?.name || ''}`}
        back={project.standalone ? '/documents' : `/project/${id}`}
      />
      <div className="view">
        <Card>
          <SectionTitle>Terms &amp; conditions</SectionTitle>
          <textarea className="fi" rows={8} style={{ marginTop: 'var(--s-3)' }} aria-label="Terms and conditions"
            value={terms} onChange={e => setTerms(e.target.value)} />
        </Card>

        <div className="split">
          <Card>
            <SectionTitle>Payment schedule</SectionTitle>
            <textarea className="fi" rows={3} style={{ marginTop: 'var(--s-3)' }} aria-label="Payment schedule"
              value={payment} onChange={e => setPayment(e.target.value)} />
          </Card>
          <Card>
            <SectionTitle>Exclusions</SectionTitle>
            <textarea className="fi" rows={3} style={{ marginTop: 'var(--s-3)' }} aria-label="Exclusions"
              placeholder="Anything explicitly not covered…"
              value={exclusions} onChange={e => setExclusions(e.target.value)} />
          </Card>
        </div>

        <div className="btn-row">
          <button className="btn" onClick={onPreview} disabled={busy}>Save &amp; preview</button>
          <button className="btn pri" onClick={onSend} disabled={busy}>Send to customer</button>
        </div>
      </div>
      {previewUrl ? (
        <PdfPreview docs={[{ label: 'Agreement', url: previewUrl }]} onClose={() => setPreviewUrl(null)} />
      ) : null}
    </>
  );
}
